import { createSlice, createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
    fetchTickerData, fetchFilteredTickers, fetchETFs, fetchXrayData, fetchUpcomingEvents
} from "../../Api/tickersApi";

const initialTickersState = {
    tickers: {}, // Stores ticker data
    filteredTickers: [],
    allFilteredTickers: [],
    badTickers: [], // Array to store symbols of tickers that don't return data
    etfs: {
      positions: {},
      status: 'idle',
      error: null,
      anchor: null,
    },
    xrayData: {}, // Object to store results from the xray API
    dividendSchedule: {
        items: [],
        status: 'idle'
    },
    status: 'idle',
    error: null,

}

const tickersSlice = createSlice({
  name: 'tickers',
  initialState: initialTickersState,
  reducers: {
      resetFilteredTickers: (state)=>{

      }
  },
  extraReducers(builder) {
    builder

      .addCase(fetchTickerData.fulfilled, (state, action) => {
        action.payload.fetchedTickers.forEach(ticker => {
          const existingData = state.tickers[ticker.symbol] || {};
          state.tickers[ticker.symbol] = { ...existingData, ...ticker };
        });
        // Update badTickers without duplicating entries
        state.badTickers = [...new Set([...state.badTickers, ...action.payload.badTickers])];
        state.status = 'succeeded';
      })
      .addCase(fetchXrayData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Assume action.payload contains the structure needed for xray data
        state.xrayData = action.payload;
      })
      .addCase(fetchFilteredTickers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.filteredTickers = action.payload;
        state.allFilteredTickers = [...state.allFilteredTickers, ...action.payload];
      })
      .addCase(fetchETFs.fulfilled, (state, action) => {
        console.log('Fetched ETFs:', action.payload);
        state.etfs.status = 'succeeded';
        action.payload.forEach(etf => {
          state.etfs.positions[etf.symbol] = { ...etf, limit: 0, weight: 0 };
        });
      })
        .addCase(fetchUpcomingEvents.fulfilled, (state, action) => {
            console.log('Fetched upcoming events:', action.payload)

        })
      .addCase(fetchFilteredTickers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchTickerData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchETFs.rejected, (state, action) => {
        state.etfs.status = 'failed';
        state.etfs.error = action.error.message;
      })
      .addCase(fetchXrayData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchETFs.pending, (state) => {state.etfs.status = 'loading'})
      .addCase(fetchFilteredTickers.pending, (state) => {state.status = 'loading'})
      .addCase(fetchTickerData.pending, (state) => {state.status = 'loading'})
      .addCase(fetchXrayData.pending, (state) => {state.status = 'loading'})
  }
});



/**
 * Selector to get multiple ticker attributes at once.
 * @param {Object} state - The global state.
 * @param {Array} symbols - An array of ticker symbols.
 * @param {String} path - The dot path to the attribute in each ticker's data.
 * @returns {Object} An object with symbols as keys and the specified attribute as values.
 */
export const selectAllTickerAttrs = createSelector(
  [state => state.tickers, (state, symbols, paths) => ({ symbols, paths })],
  (tickers, { symbols, paths }) => {
    return symbols.reduce((acc, symbol) => {
      const tickerData = tickers.tickers[symbol];
      if (!tickerData) {
        return acc;
      }

      const attributesForSymbol = paths.reduce((attrAcc, path) => {
        const attrValue = _.get(tickerData, path);
        if (attrValue !== undefined) { // Check explicitly for undefined to allow null values
          _.set(attrAcc, path, attrValue); // Use _.set to handle dot paths correctly
        }
        return attrAcc;
      }, {});

      acc[symbol] = attributesForSymbol;
      return acc;
    }, {});
  }
);



export const selectDividendSchedule = (state) => {
  return state.tickers.dividendSchedule.items?.tickers || [];
};


export default tickersSlice.reducer;